import React, { useCallback, useState, useRef, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { flushSync } from 'react-dom';
function ZeroCreditsCard({
isTrial = false,
membershipTo = '/estate-sales-pipeline/member-maint2',
inventoryTo = '/estate-sales-pipeline/inventory',
}) {
return (
<section className="mx-auto max-w-xl rounded-lg bg-white p-8 text-center shadow-lg ring-1 ring-gray-200">
<h2 className="text-2xl font-semibold text-gray-900">
{isTrial ? 'Your 5 free uploads are used.' : "You're out of Upload Credits."}
</h2>
<p className="mt-2 text-base text-gray-700">Purchase credits to upload images.</p>
<div className="mt-6 flex justify-center">
<Link
to={membershipTo}
className="inline-flex items-center justify-center rounded-md bg-blue-600 px-6 py-3 text-sm font-semibold text-white hover:bg-blue-700"
>
          Purchase Upload Credits
</Link>
</div>
<p className="mt-3 text-sm text-gray-600">
        Or view your <Link to={inventoryTo} className="font-semibold underline">Inventory</Link>.
</p>
</section>
  );
}
export default function UploaderApp() {
const { user, membership, loading: authLoading, supabase, fetchWrapper } = useAuth();
const navigate = useNavigate();
const [files, setFiles] = useState([]);
const [status, setStatus] = useState('');
const [uploading, setUploading] = useState(false);
const [hasDuplicates, setHasDuplicates] = useState(false);
const [sales, setSales] = useState([]);
const [selectedSaleId, setSelectedSaleId] = useState('');
const [sections, setSections] = useState([]);
const [selectedSectionId, setSelectedSectionId] = useState('');
const [customSectionName, setCustomSectionName] = useState('');
const [showCustomInput, setShowCustomInput] = useState(false);
const [droppedFiles, setDroppedFiles] = useState(0); // NEW: For warning about dropped files
const [maxBatchSize, setMaxBatchSize] = useState(0);
const [tiers, setTiers] = useState([]);
const [currentBatch, setCurrentBatch] = useState(0);
const [creditErrorMsg, setCreditErrorMsg] = useState('');
const [uploadsBlocked, setUploadsBlocked] = useState(false);
const [uploadsBlockedMessage, setUploadsBlockedMessage] = useState('');
const retryFileMapRef = useRef(new Map());
const imagesAvailable = membership?.total_images_avail ?? 0;
const isOnFreeTrial = Boolean(
membership?.is_on_trial ||
      (typeof membership?.tier === 'string' && membership.tier.toLowerCase() === 'trial')
  );
useEffect(() => {
console.log('🔄 UploaderApp MOUNTED', { timestamp: Date.now(), uploadResults: uploadResults.length, successMessage: !!successMessage });
return () => console.log('💥 UploaderApp UNMOUNTED', { timestamp: Date.now() });
}, []); // Runs on mount/unmount
// NEW: Persist across unmount/remount (serialize only name/status)
const STORAGE_KEY = 'scucci-uploader-history';
const [uploadResults, setUploadResults] = useState(() => {
try {
const saved = sessionStorage.getItem(STORAGE_KEY);
if (!saved) return [];
const parsed = JSON.parse(saved);
// Restore with dummy file {name} for grid
return parsed.uploadResults?.map(r => ({
file: { name: r.fileName }, // Dummy for .name access
status: r.status
    })) || [];
  } catch {
return [];
  }
});
const [successMessage, setSuccessMessage] = useState(() => {
try {
const saved = sessionStorage.getItem(STORAGE_KEY);
return saved ? JSON.parse(saved).successMessage || '' : '';
  } catch {
return '';
  }
});
// NEW: Auto-save (serialize fileName only)
useEffect(() => {
const serializableResults = uploadResults.map(r => ({
fileName: r.file.name, // Extract name only
status: r.status
  }));
const timeoutId = setTimeout(() => {
sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
uploadResults: serializableResults,
successMessage
    }));
  }, 500);
return () => clearTimeout(timeoutId);
}, [uploadResults, successMessage]);
// Credit error message management
useEffect(() => {
  if (files.length > imagesAvailable && files.length > 0) {
    const toRemove = files.length - imagesAvailable;
    setCreditErrorMsg(`You only have ${imagesAvailable} credits left, remove ${toRemove}`);
  } else {
    setCreditErrorMsg('');
  }
}, [files.length, imagesAvailable]);
// --- EFFECTS ---
useEffect(() => {
console.log('UploaderApp useEffect:', { authLoading, user, membership });
if (authLoading || !membership) {
console.log('Waiting for auth or membership');
return;
    }
if (!user) {
console.log('No user, setting status');
setStatus('❌ Please sign in.');
return;
    }
let isActive = true;
const currentDate = new Date();
if (membership.end_date && new Date(membership.end_date) < currentDate) {
isActive = false;
    }
console.log('Computed membership active:', isActive, { end_date: membership.end_date, currentDate });
if (!isActive) {
console.log('Inactive membership in UploaderApp', { membership, isActive });
setStatus('❌ Active membership required.');
    } else if (imagesAvailable <= 0) {
setStatus('');
    }
  }, [user, membership, authLoading, imagesAvailable]);
// Global failed uploads guard (temporary shutdown)
useEffect(() => {
  if (!fetchWrapper) return;
  const checkFailedUploads = async () => {
    try {
      const [countRes, flagRes] = await Promise.all([
        fetchWrapper(`${process.env.REACT_APP_API_URL}/supabase/failed_uploads_count`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      }),
        fetchWrapper(`${process.env.REACT_APP_API_URL}/supabase/rpc/select_system_flag`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ p_key: 'pipeline_uploads_disabled' })
        })
      ]);
      if (!countRes || !flagRes) return;
      const countData = await countRes.json();
      const count = Number(countData?.count || 0);
      const flagValue = await flagRes.json();
      const isFlagged = Boolean(flagValue);
      if (isFlagged || count > 10) {
        const message = 'Image Uploads are Temporarily Unavailable. Please Try Later';
        setUploadsBlocked(true);
        setUploadsBlockedMessage(message);
        setStatus(message);
      }
    } catch (error) {
      console.error('Failed uploads count check failed:', error);
    }
  };
  checkFailedUploads();
}, [fetchWrapper]);
useEffect(() => {
  if (!uploadsBlocked) return;
  const timer = setTimeout(() => {
    navigate('/estate-sales-pipeline/userdashboard');
  }, 15000);
  return () => clearTimeout(timer);
}, [uploadsBlocked, navigate]);
// --- CALLBACKS & HELPERS ---
const fetchSalesAndCosts = useCallback(
async (loadedTiers = tiers) => {
if (!user || !membership || !loadedTiers.length) return;
try {
const tierData = loadedTiers.find((t) => t.tier.toLowerCase() === membership.tier.toLowerCase());
if (!tierData) {
setStatus('❌ No tier found for membership.');
return;
        }
setMaxBatchSize(tierData.max_images_uploads || 0);
const salesRes = await fetchWrapper(`${process.env.REACT_APP_API_URL}/supabase/rpc/select_all_sales`, {
method: 'POST',
headers: { 'Content-Type': 'application/json' },
body: JSON.stringify({ p_client_id: user.id }),
        });
if (!salesRes) return;
const salesData = await salesRes.json();
if (!salesData || salesData.length === 0) {
setStatus('❌ No sales found.');
return;
        }
setSales(salesData);
setStatus('Choose a Sale to get started.');
      } catch (error) {
console.error('Fetch error:', error);
setStatus(`❌ Failed to load: ${error.message}`);
      }
    },
    [user, membership, fetchWrapper, tiers]
  );
useEffect(() => {
const fetchTiers = async () => {
try {
const tiersRes = await fetchWrapper(`${process.env.REACT_APP_API_URL}/supabase/rpc/get_tiers`, {
method: 'POST',
headers: { 'Content-Type': 'application/json' },
body: JSON.stringify({}),
        });
if (!tiersRes) return;
const allTiers = await tiersRes.json();
setTiers(allTiers);
fetchSalesAndCosts(allTiers);
      } catch (error) {
console.error('Tiers fetch error:', error);
setStatus(`❌ Failed to load tiers: ${error.message}`);
      }
    };
if (user && membership && tiers.length === 0) {
fetchTiers();
    }
  }, [user, membership, tiers.length, fetchWrapper, fetchSalesAndCosts]);
const onDrop = useCallback(
    (acceptedFiles, fileRejections) => {
if (uploadsBlocked) {
setStatus(uploadsBlockedMessage || 'Image Uploads are Temporarily Unavailable. Please Try Later');
return;
}
setHasDuplicates(false);
setUploadResults([]);
setDroppedFiles(0); // Reset dropped
// Process accepted files first (always add them, handle dups/thresholds below)
setFiles((prev) => {
const prevNames = new Set(prev.map((f) => f.name.toLowerCase()));
const newFiles = acceptedFiles.filter((f) => !prevNames.has(f.name.toLowerCase()));
if (newFiles.length < acceptedFiles.length) setHasDuplicates(true);
const totalFiles = [...prev, ...newFiles];
// Check thresholds
if (totalFiles.length > maxBatchSize) {
setStatus(`⚠️ Too many files (max ${maxBatchSize}). Remove excess.`);
        } else if (totalFiles.length > 0) {
setStatus('Ready to upload.');
        } else {
setStatus('Drag & drop or choose files.');
        }
return totalFiles;
      });
// FIXED: Handle rejections AFTER processing accepted (no early return or clear)
if (fileRejections.length > 0) {
const tooManyRejections = fileRejections.filter((rej) =>
rej.errors.some((err) => err.code === 'too-many-files')
        ).length;
const otherRejections = fileRejections.length - tooManyRejections;
if (tooManyRejections > 0) {
setDroppedFiles(tooManyRejections);
        }
if (otherRejections > 0) {
setStatus((prev) => {
const base = prev ? `${prev} ` : '';
return `${base}⚠️ ${otherRejections} file(s) rejected (wrong type).`;
          });
        }
      }
    },
    [maxBatchSize]
  );
const fetchSections = useCallback(
async (saleId) => {
if (!saleId || !user) {
setSections([]);
return;
      }
try {
console.log(`Fetching sections for sale ${saleId}...`);
const assignedRes = await fetchWrapper(
`${process.env.REACT_APP_API_URL}/supabase/rpc/select_sale_sections_by_sale_id`,
          {
method: 'POST',
headers: { 'Content-Type': 'application/json' },
body: JSON.stringify({ p_sale_id: saleId }),
          }
        );
if (!assignedRes) throw new Error('Assigned fetch failed');
const assignedData = await assignedRes.json();
const globalsRes = await fetchWrapper(`${process.env.REACT_APP_API_URL}/supabase/rpc/get_section_descs`, {
method: 'POST',
headers: { 'Content-Type': 'application/json' },
body: JSON.stringify({}),
        });
if (!globalsRes) throw new Error('Globals fetch failed');
const globalsData = await globalsRes.json();
const assignedNames = new Set(assignedData.map((s) => s.name));
const availableGlobals = globalsData.filter((g) => !assignedNames.has(g.name));
const mergedSections = [
...assignedData.map((s) => ({ ...s, is_assigned: true })),
...availableGlobals.map((g) => ({ ...g, is_assigned: false })),
        ];
mergedSections.sort((a, b) => {
if (a.is_assigned !== b.is_assigned) return a.is_assigned ? -1 : 1;
return a.name.localeCompare(b.name);
        });
setSections(mergedSections);
setSelectedSectionId('');
if (mergedSections.length === 0) {
setStatus('⚠️ No sections available. Pick a global or create custom via "Other".');
        } else {
setStatus('');
        }
console.log('Merged sections:', mergedSections);
      } catch (error) {
console.error('Sections fetch error:', error);
setStatus(`❌ Failed to load sections: ${error.message}`);
setSections([]);
      }
    },
    [user, fetchWrapper]
  );
useEffect(() => {
if (files.length > 0) {
setUploadResults([]);
setSuccessMessage('');
    }
  }, [files.length]);
useEffect(() => {
if (selectedSaleId) {
setUploadResults([]);
setSuccessMessage('');
    }
  }, [selectedSaleId]);
useEffect(() => {
if (selectedSectionId && !showCustomInput) {
setUploadResults([]);
setSuccessMessage('');
    }
  }, [selectedSectionId, showCustomInput]);
useEffect(() => {
if (selectedSaleId) {
fetchSections(selectedSaleId);
    } else {
setSections([]);
setSelectedSectionId('');
    }
  }, [selectedSaleId, fetchSections]);
const handleCustomSection = async () => {
if (!customSectionName.trim() || !selectedSaleId) {
setStatus('❌ Please enter a section name and select a sale.');
return;
    }
try {
setStatus('Creating custom section...');
const response = await fetchWrapper(`${process.env.REACT_APP_API_URL}/supabase/rpc/insert_sale_section`, {
method: 'POST',
headers: { 'Content-Type': 'application/json' },
body: JSON.stringify({
p_sale_id: selectedSaleId,
p_name: customSectionName.trim(),
        }),
      });
if (!response) throw new Error('Insert failed');
const newSectionId = await response.json();
console.log('Created custom section ID:', newSectionId);
await fetchSections(selectedSaleId);
setSelectedSectionId(newSectionId);
setCustomSectionName('');
setShowCustomInput(false);
setStatus(`✅ "${customSectionName}" created and selected.`);
setTimeout(() => setStatus('Load images to this sale'), 3000);
    } catch (error) {
console.error('Custom section error:', error);
setStatus(`❌ Failed to create: ${error.message}`);
    }
  };
const handleSectionChange = async (e) => {
const value = e.target.value;
setSelectedSectionId(value);
setShowCustomInput(value === 'other');
setCustomSectionName('');
if (value === 'other') return;
  };
const { getRootProps, getInputProps, open: openFileDialog, isDragActive } = useDropzone({
onDrop,
maxFiles: maxBatchSize,
disabled: uploadsBlocked || !maxBatchSize || imagesAvailable <= 0,
noClick: true,
noKeyboard: true,
multiple: true,
accept: {
'image/jpeg': ['.jpeg', '.jpg'],
'image/bmp': ['.bmp'],
    },
  });
const handleOpenFileDialog = () => {
setHasDuplicates(false);
setUploadResults([]);
setDroppedFiles(0);
openFileDialog();
  };
const removeFile = (name) => {
setFiles((prev) => {
const newFiles = prev.filter((file) => file.name !== name);
if (newFiles.length > maxBatchSize) {
setStatus(`⚠️ Too many files (max ${maxBatchSize}). Remove excess.`);
      } else if (newFiles.length > 0) {
setStatus('Ready to upload.');
      } else {
setStatus('Drag & drop or choose files.');
      }
return newFiles;
    });
setUploadResults((prev) => prev.filter((result) => result.file.name !== name));
setHasDuplicates(false);
  };
const slugify = (name) => name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
const uploadFile = async (file, existingFiles, resolvedSectionId) => {
const fileName = file.name;
if (existingFiles.some((f) => f.name === fileName)) {
throw new Error('Duplicate');
    }
setUploadResults((prev) =>
prev.map((r) => (r.file.name === fileName ? { ...r, status: 'uploading' } : r))
    );
let sectionNameForSlug = '';
if (showCustomInput) {
if (!customSectionName.trim()) {
throw new Error('Custom section name required');
      }
sectionNameForSlug = customSectionName.trim();
    } else {
const selectedSection = sections.find((s) => s.id === selectedSectionId);
if (!selectedSection) {
throw new Error('No section selected');
      }
sectionNameForSlug = selectedSection.name;
    }
const sectionSlug = slugify(sectionNameForSlug || 'default');
const buildFormData = () => {
const formData = new FormData();
formData.append('file', file);
formData.append('method', 'UPLOAD');
formData.append('bucket', 'uploads');
formData.append('filePath', `${user.id}/${selectedSaleId}/${sectionSlug}/${file.name}`);
formData.append('schema', 'estate');
formData.append('endpoint', 'insert_estate_user_upload_log');
formData.append(
'data',
JSON.stringify({
p_client_id: user.id,
p_sale_id: selectedSaleId,
p_filename: file.name,
p_uploaded_at: new Date().toISOString(),
p_section_id: resolvedSectionId || null,
p_public_url: null,                 // Updated later in VisionWriter
p_image_width_px: null,             // Until VisionWriter fills real values
p_image_height_px: null
      })
    );
return formData;
  };
const maxUploadAttempts = 3;
for (let attempt = 1; attempt <= maxUploadAttempts; attempt++) {
try {
const response = await fetchWrapper(`${process.env.REACT_APP_API_URL}/supabase/`, {
method: 'POST',
body: buildFormData(),
      });
if (!response.ok) {
const errText = await response.text();
throw new Error(`Upload failed: ${errText}`);
      }
const result = await response.json();
console.log(
`✅ Uploaded ${fileName} to section "${sectionNameForSlug}" (ID: ${resolvedSectionId}, slug: ${sectionSlug})`,
result
      );
return result;
    } catch (error) {
const message = error?.message || '';
const isTransient =
message.includes('Failed to fetch') ||
message.includes('NetworkError') ||
message.includes('ERR_HTTP2') ||
message.includes('ERR_SSL');
if (isTransient && attempt < maxUploadAttempts) {
const delay = 500 * attempt;
console.warn(`⚠️ Transient upload error for ${fileName}; retry ${attempt + 1}/${maxUploadAttempts} in ${delay}ms`);
await new Promise((resolve) => setTimeout(resolve, delay));
continue;
      }
console.error(`❌ Upload error for ${fileName}:`, error);
throw error;
    }
  }
  };
const handleUpload = async () => {
// --- Initial Checks ---
if (!user) {
setStatus('❌ Please sign in.');
return;
    }
if (!selectedSaleId) {
setStatus('❌ Select a sale.');
return;
    }
if (!selectedSectionId && !showCustomInput) {
setStatus('❌ Select or create a section.');
return;
    }
// --- Threshold Checks ---
if (files.length > maxBatchSize) {
setStatus(`⚠️ Max ${maxBatchSize} files. Remove excess.`);
return;
    }
// --- Setup Upload State ---
const filesToUpload = [...files];
retryFileMapRef.current = new Map(filesToUpload.map((file) => [file.name, file]));
setFiles([]);
setUploading(true);
setStatus('Preparing upload...');
setHasDuplicates(false);
// Initialize results with file objects and 'pending' status
setUploadResults(filesToUpload.map((file) => ({ file: file, status: 'pending' })));
// --- Setup Batch State ---
const batchSize = 3;
const batchDelay = 2000;
const calculatedTotalBatches = Math.ceil(filesToUpload.length / batchSize);
setCurrentBatch(0);
// --- Resolve Section ID ---
let resolvedSectionId = selectedSectionId;
let sectionNameForSlug = '';
if (showCustomInput) {
if (!customSectionName.trim()) {
setUploading(false);
setStatus('❌ Custom section name required');
return;
      }
sectionNameForSlug = customSectionName.trim();
try {
console.log('Creating/Assigning custom section:', sectionNameForSlug);
const response = await fetchWrapper(`${process.env.REACT_APP_API_URL}/supabase/rpc/insert_sale_section`, {
method: 'POST',
headers: { 'Content-Type': 'application/json' },
body: JSON.stringify({
p_sale_id: selectedSaleId,
p_name: sectionNameForSlug,
          }),
        });
if (!response.ok) {
const errorData = await response.json();
throw new Error(errorData.message || 'Insert/Assign failed');
        }
resolvedSectionId = await response.json();
console.log('Assigned section ID for custom section:', resolvedSectionId);
await fetchSections(selectedSaleId);
setSelectedSectionId(resolvedSectionId);
setShowCustomInput(false);
setCustomSectionName('');
      } catch (error) {
console.error('Custom section insert/assign error:', error);
setUploading(false);
setStatus(`❌ Upload failed: Could not prepare section. Check network connection.`);
return;
      }
    } else if (selectedSectionId) {
const selectedSection = sections.find((s) => s.id === selectedSectionId);
if (!selectedSection) {
setUploading(false);
setStatus('❌ Internal error: Selected section not found.');
return;
      }
sectionNameForSlug = selectedSection.name;
if (!selectedSection.is_assigned) {
try {
console.log('Assigning global section for upload:', selectedSection.name);
const response = await fetchWrapper(`${process.env.REACT_APP_API_URL}/supabase/rpc/insert_sale_section`, {
method: 'POST',
headers: { 'Content-Type': 'application/json' },
body: JSON.stringify({
p_sale_id: selectedSaleId,
p_name: selectedSection.name,
            }),
          });
if (!response.ok) {
const errorData = await response.json();
throw new Error(errorData.message || 'Assign failed');
          }
resolvedSectionId = await response.json();
console.log('Assigned section ID from global insert:', resolvedSectionId);
await fetchSections(selectedSaleId);
        } catch (error) {
console.error('Global section assign error:', error);
setUploading(false);
setStatus(`❌ Upload failed: Could not prepare section. Check network connection.`);
return;
        }
      }
    } else {
setUploading(false);
setStatus('❌ Section selection error.');
return;
    }
// --- Prepare for Upload Loop ---
const sectionSlug = slugify(sectionNameForSlug || 'default');
const { data: existingFiles } = await supabase.storage
      .from('uploads')
      .list(`${user.id}/${selectedSaleId}/${sectionSlug}`);
let successCount = 0,
errorCount = 0,
duplicateCount = 0;
// --- Upload Loop ---
for (let i = 0; i < filesToUpload.length; i += batchSize) {
const currentBatchNumber = Math.floor(i / batchSize) + 1;
setCurrentBatch(currentBatchNumber);
console.log(`Batch ${currentBatchNumber}/${calculatedTotalBatches}`);
setStatus(`Uploading Batch ${currentBatchNumber} of ${calculatedTotalBatches}...`);
const batch = filesToUpload.slice(i, i + batchSize);
// Set status to 'uploading' for files in the current batch
setUploadResults((prev) =>
prev.map((r) =>
batch.some((bFile) => bFile.name === r.file.name) && r.status === 'pending'
? { ...r, status: 'uploading' }
: r
        )
      );
// Process the batch uploads
const uploadPromises = batch.map((file) =>
uploadFile(file, existingFiles || [], resolvedSectionId)
      );
const results = await Promise.allSettled(uploadPromises);
// Local counters for this batch's results
let batchSuccess = 0;
let batchError = 0;
let batchDuplicate = 0;
// Update results table based on settled promises
results.forEach((result, index) => {
const file = batch[index];
if (result.status === 'fulfilled') {
batchSuccess++;
setUploadResults((prev) =>
prev.map((r) => (r.file.name === file.name ? { ...r, status: 'completed' } : r))
          );
        } else {
const errorStr = result.reason?.message || '';
if (errorStr.includes('Duplicate')) {
batchDuplicate++;
setUploadResults((prev) =>
prev.map((r) => (r.file.name === file.name ? { ...r, status: 'duplicated not uploaded' } : r))
            );
          } else {
            batchError++;
            const errorMsg = 'Image failed to upload, please reselect/upload this image';
            setUploadResults((prev) =>
              prev.map((r) => (r.file.name === file.name ? { ...r, status: errorMsg } : r))
            );
          }
        }
      });
// Update total counters
successCount += batchSuccess;
errorCount += batchError;
duplicateCount += batchDuplicate;
// Optional delay before next batch
if (i + batchSize < filesToUpload.length) {
await new Promise((resolve) => setTimeout(resolve, batchDelay));
      }
    } // --- End of For Loop ---
// --- Log Batch Upload (if successful uploads occurred) ---
if (successCount > 0) {
try {
const saleName = sales.find((s) => s.id === selectedSaleId)?.sale_name || 'Unknown Sale';
const sectionName = sectionNameForSlug || 'Unknown Section';
console.log(`Logging batch of ${successCount} images to member_usage_log.`);
await fetchWrapper(`${process.env.REACT_APP_API_URL}/supabase/rpc/log_batch_upload`, {
method: 'POST',
headers: { 'Content-Type': 'application/json' },
body: JSON.stringify({
p_user_id: user.id,
p_upload_count: successCount,
p_sale_name: saleName,
p_section_name: sectionName,
          }),
        });
      } catch (err) {
console.error('CRITICAL: Failed to log upload batch to member_usage_log:', err);
      }
    }
// --- End Log Batch Upload ---
// --- Final Status Update (force early sync for storage) ---
let statusParts = [`Upload complete: ${successCount} of ${filesToUpload.length} succeeded.`];
if (errorCount > 0) statusParts.push(`${errorCount} failed.`);
if (duplicateCount > 0) statusParts.push(`${duplicateCount} duplicates skipped.`);
const successMsg = statusParts.join(' ');
// Explicit save before delay (bypasses debounce for reliability)
const serializableResults = uploadResults.map(r => ({
fileName: r.file.name,
status: r.status
}));
sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
uploadResults: serializableResults,
successMessage: successMsg
}));
// Update state (React will re-render grid/message immediately)
setUploading(false);
setStatus('');
setSuccessMessage(successMsg);
setCurrentBatch(0);
console.log('📝 Set success/grid', { successMsg, resultsCount: uploadResults.length }); // Should now show >0 if loop finished
// --- Smooth Delay: Show progress, then refresh credits ---
setStatus('Updating credits...'); // Interim message (green tint via className)
setTimeout(() => {
console.log('⏰ About to dispatch membershipUpdated...');
window.dispatchEvent(new Event('membershipUpdated'));
// Clear interim after dispatch
setTimeout(() => setStatus(''), 1000);
}, 1000); // 2s: Balances visibility + speed
  };
const formatSaleDate = (dateString) => {
if (!dateString) return 'N/A';
const utcDate = new Date(dateString);
const year = utcDate.getUTCFullYear();
const month = utcDate.getUTCMonth();
const day = utcDate.getUTCDate();
return new Intl.DateTimeFormat('en-US', {
month: 'short',
day: 'numeric',
year: 'numeric',
    }).format(new Date(year, month, day));
  };
// FIXED: Enhanced disabled logic to prevent upload if custom input empty
const uploadDisabled =
files.length === 0 ||
uploading ||
files.length > maxBatchSize ||
files.length > imagesAvailable ||
    (files.length > 0 && !selectedSectionId && !showCustomInput) ||
    (showCustomInput && !customSectionName.trim());
return (
<div className="min-h-screen flex flex-col bg-gray-100 pt-20">
<div className="flex-grow flex flex-col">
<div className="w-full max-w-4xl mx-auto">
<div className="bg-white shadow-md rounded-md p-4 mb-6 text-center">
<h1 className="text-2xl font-bold mb-2">Scucci Uploader</h1>
<div className="flex justify-center divide-x divide-gray-300 space-x-4 text-sm text-gray-700">
<p className="px-4">
<strong>Max Batch Uploads:</strong> {maxBatchSize}
</p>
<p className="px-4">
<strong>Total Upload Credits:</strong> {imagesAvailable}
</p>
</div>
{imagesAvailable > 0 && imagesAvailable < 10 && (
<div className="mt-3 mx-auto inline-flex items-center gap-2 rounded-md bg-yellow-50 px-3 py-2 text-sm font-semibold text-yellow-900 ring-1 ring-yellow-200">
<span aria-hidden>⚠️</span>
<span>
                  You're running low! You have {imagesAvailable} upload{' '}
{imagesAvailable === 1 ? 'credit' : 'credits'} left.{' '}
<Link
to="/estate-sales-pipeline/member-maint2"
className="text-blue-500 underline hover:text-blue-700 font-medium"
>
                    Click here to add credits
</Link>
</span>
</div>
            )}
</div>
<select
value={selectedSaleId}
onChange={(e) => setSelectedSaleId(e.target.value)}
className="block w-64 mx-auto border border-gray-300 p-2 rounded-md mb-4"
>
<option value="">Select a Sale</option>
{sales
              .filter((sale) => sale.status !== 'deleted')
              .sort((a, b) => {
                const aTime = new Date(a?.start_date || 0).getTime();
                const bTime = new Date(b?.start_date || 0).getTime();
                return bTime - aTime;
              })
              .map((sale) => (
<option key={sale.id} value={sale.id}>
{sale.sale_name} - {formatSaleDate(sale.start_date)}
</option>
              ))}
</select>
{selectedSaleId && (
<div className="text-center mb-4">
{!selectedSectionId && !showCustomInput && (
<label className="block text-sm font-bold text-gray-600 mb-2">Select a Section</label>
              )}
<select
value={selectedSectionId}
onChange={handleSectionChange}
className="block w-80 mx-auto border border-gray-300 p-2 rounded-md text-sm"
disabled={sections.length === 0 && !showCustomInput}
>
<option value="">Pick or create a section</option>
{sections.map((section) => (
<option key={section.id} value={section.id}>
{section.name} {section.is_assigned ? '🏷️' : ''}
</option>
                ))}
{sections.length === 0 && (
<option value="" disabled>
                    No sections yet—pick a global above or "Other"
</option>
                )}
<option value="other">Other (Custom Section)</option>
</select>
{showCustomInput && (
<div className="mt-2 p-3 bg-gray-50 rounded-md">
<input
type="text"
value={customSectionName}
onChange={(e) => setCustomSectionName(e.target.value)}
placeholder="e.g., XMAS DECOR"
className="block w-64 mx-auto border border-gray-300 p-2 rounded-md mb-2"
/>
<div className="flex justify-center space-x-2">
<button
onClick={handleCustomSection}
disabled={!customSectionName.trim()}
className="px-4 py-1 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50"
>
                      Create & Select
</button>
<button
onClick={() => {
setShowCustomInput(false);
setCustomSectionName('');
setSelectedSectionId('');
                      }}
className="px-4 py-1 bg-gray-500 text-white rounded-md hover:bg-gray-600"
>
                      Cancel
</button>
</div>
</div>
              )}
{sections.length === 0 && !showCustomInput && (
<p className="text-xs text-gray-500 mt-1">Start with a global like "Kitchen" or create custom.</p>
              )}
</div>
          )}
{status && (
<p
className={`mt-4 mb-4 text-sm font-bold text-center ${
status.startsWith('Upload complete') ? 'text-green-600'
                : status.startsWith('Choose a Sale') ? 'text-gray-600'
                : (status.startsWith('❌') || status.startsWith('⚠️')) ? 'text-red-600'
                : 'text-gray-600'
}`}
>
{status}
</p>
          )}
{uploadsBlockedMessage && (
<div className="fixed top-18 left-1/2 -translate-x-1/2 z-50 bg-red-600 text-white px-4 py-2 rounded shadow-lg text-sm font-semibold">
{uploadsBlockedMessage}
</div>
          )}
{successMessage && (
<div className="mt-4 p-3 bg-green-50 border border-green-200 rounded text-sm text-center text-green-700 font-semibold mb-4">
{successMessage}{' '}
</div>
          )}
{imagesAvailable <= 0 ? (
<ZeroCreditsCard isTrial={isOnFreeTrial} />
          ) : (
<>
<div
{...getRootProps()}
className={`border-2 border-dashed p-6 rounded-md text-center transition ${
isDragActive ? 'border-blue-500 bg-blue-100' : 'border-blue-300 bg-blue-50'
}`}
>
<input {...getInputProps()} />
<div className="text-sm text-gray-700 mb-4">
<p className="font-medium">
{isDragActive ? 'Drop your photos here' : 'Drag & drop your photos here'}
</p>
{!isDragActive && <p className="mt-1">or</p>}
</div>
<button
onClick={handleOpenFileDialog}
className="block w-64 mx-auto bg-blue-500 text-white px-6 py-3 rounded-md hover:bg-blue-600 transition disabled:opacity-50"
type="button"
disabled={uploading}
>
                  Click to Select Images
</button>
</div>
<div className="mt-4 text-center">
{/* NEW: Warning for dropped files */}
{droppedFiles > 0 && (
<p className="text-yellow-500 mb-2">{droppedFiles} excess file(s) dropped (max {maxBatchSize}).</p>
                )}
{hasDuplicates && <p className="text-red-500 mb-2">Dups dropped.</p>}
{files.length > maxBatchSize && (
<p className="text-red-500 mb-2">Too many files (max {maxBatchSize}).</p>
                )}
<button
disabled={uploadDisabled}
className="block w-64 mx-auto bg-blue-500 text-white px-6 py-3 rounded-md hover:bg-blue-700 transition disabled:opacity-50"
onClick={handleUpload}
>
{uploading ? 'Uploading...' : 'Upload & Find Items'}
</button>
                {uploadResults.length > 0 &&
                  !uploading &&
                  uploadResults.some((r) => r.status.startsWith('error')) && (
                    <div className="mt-2 text-center text-sm text-red-600 font-semibold">
                      Image failed to upload, please reselect/upload this image
                    </div>
                  )}
</div>
{!uploading && files.length > 0 && (
<div className="mt-4 space-y-2">
{creditErrorMsg && (
  <p className="text-red-500 mb-2 text-center font-bold">
    {creditErrorMsg}
  </p>
)}
<div className="flex justify-end pb-2 border-b border-gray-200">
<button
className="text-xs text-red-600 hover:underline font-semibold"
onClick={() => {
setFiles([]);
setStatus('File list cleared.');
                      }}
type="button"
>
                      Clear All Files
</button>
</div>
{files.map((file) => (
<div key={file.name} className="flex justify-between items-center p-2 bg-gray-100 rounded-md">
<span className="text-sm text-gray-800">{file.name}</span>
<button
className="text-xs text-red-600 hover:underline"
onClick={() => removeFile(file.name)}
type="button"
>
                        Remove
</button>
</div>
                  ))}
</div>
              )}
{uploadResults.length > 0 && (
<div className="mt-6 space-y-4 flex-grow">
<h3 className="text-lg font-semibold text-center">Upload Status</h3>
<div className="overflow-x-auto">
<table className="w-full text-sm text-left text-gray-800">
<thead className="text-xs text-gray-700 uppercase bg-gray-200">
<tr>
<th className="px-4 py-3 w-[50%]">Filename</th>
<th className="px-4 py-3">Status</th>
</tr>
</thead>
<tbody>
{uploadResults.map((result, index) => (
<tr key={`${result.file.name}:${index}`} className="bg-white border-b">
<td className="px-4 py-2 w-[50%]">{result.file.name}</td>
<td className="px-4 py-2">
<span
className={`font-semibold ${
result.status === 'completed'
                                    ? 'text-green-600'
                                    : result.status.startsWith('error')
                                    ? 'text-red-600'
                                    : 'text-gray-600'
}`}
>
{result.status}
</span>
</td>
</tr>
                        ))}
</tbody>
</table>
</div>
</div>
              )}
</>
          )}
</div>
</div>
</div>
  );
}
